import { useCallback, useEffect } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';

function normalizeUrl(url) {
  if (!url) return null;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

const DEVICE_COLLECT_URL = normalizeUrl(window.__CONFIG__?.DEVICE_INTELLIGENCE_URL)
  || (process.env.NODE_ENV === 'production'
    ? "https://trust.pipl.com/api/device/attributes"
    : null);
const RSA_PUBLIC_KEY_PEM = window.__CONFIG__?.DEVICE_INTELLIGENCE_RSA_PUBLIC_KEY;
const SCHEMA_VERSION = window.__CONFIG__?.DEVICE_INTELLIGENCE_SCHEMA_VERSION || 2;
const SDK_TYPE = "web";
const SDK_VERSION = "0.0.1";
const SITE_ID = "Accounts";

async function importPublicKey(pem) {
  const pemContents = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s/g, '');

  const binaryDer = Uint8Array.from(atob(pemContents), c => c.charCodeAt(0));

  return crypto.subtle.importKey(
    'spki',
    binaryDer.buffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  );
}

async function generateAESKey() {
  return crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt']
  );
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function encryptWithAES(aesKey, data) {
  const encoder = new TextEncoder();
  const dataBytes = encoder.encode(JSON.stringify(data));

  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    aesKey,
    dataBytes
  );

  return {
    ciphertext: arrayBufferToBase64(ciphertext),
    iv: arrayBufferToBase64(iv),
  };
}

async function encryptAESKeyWithRSA(aesKey, rsaPublicKey) {
  const rawKey = await crypto.subtle.exportKey('raw', aesKey);

  const encryptedKey = await crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    rsaPublicKey,
    rawKey
  );

  return arrayBufferToBase64(encryptedKey);
}

function isEncryptionSupported() {
  return !!(crypto && crypto.subtle);
}

async function encryptPayload(payload) {
  try {
    const rsaPublicKey = await importPublicKey(RSA_PUBLIC_KEY_PEM);

    const aesKey = await generateAESKey();

    const { ciphertext, iv } = await encryptWithAES(aesKey, payload);

    const encryptedKey = await encryptAESKeyWithRSA(aesKey, rsaPublicKey);

    return {
      payload: ciphertext,
      key: encryptedKey,
      iv: iv,
      version: '1.0',
    };
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt payload');
  }
}

function normalizeBase64(base64) {
  return base64.replace(/^data:image\/png;base64,/, '');
}

async function hashBase64Image(base64Image) {
  const normalizedBase64 = normalizeBase64(base64Image);

  const encoder = new TextEncoder();
  const data = encoder.encode(normalizedBase64);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  return [...new Uint8Array(hashBuffer)]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

const useDeviceIntelligence = (user) => {
  const collectAndSendDeviceIntelligence = useCallback(() => {
    if (!DEVICE_COLLECT_URL || !RSA_PUBLIC_KEY_PEM) {
      return;
    }

    let thumbmarkPromise;
    let fingerprintPromise;
    
    try {
      thumbmarkPromise = new Thumbmark({ logging: false }).get();
      fingerprintPromise = FingerprintJS.load({ monitoring: false }).then((fp) => fp.get());
    } catch (initError) {
      console.error('Failed to initialize fingerprinting libraries:', initError);
      return;
    }

    thumbmarkPromise
      .then((thumbmarkResult) => {
        return fingerprintPromise.then((fingerprintResult) => {
          return { thumbmarkResult, fingerprintResult };
        });
      })
      .then(({ thumbmarkResult, fingerprintResult }) => {
        let cookie_device_id = localStorage.getItem("cookie_device_id");
        if (!cookie_device_id) {
          cookie_device_id = crypto.randomUUID();
          localStorage.setItem("cookie_device_id", cookie_device_id);
        }

        const hashPromises = [];
        let hashedGeometry = fingerprintResult?.components?.canvas?.value?.geometry;
        let hashedText = fingerprintResult?.components?.canvas?.value?.text;

        if (fingerprintResult?.components?.canvas?.value?.geometry) {
          hashPromises.push(
            hashBase64Image(fingerprintResult.components.canvas.value.geometry).then((hash) => {
              hashedGeometry = hash;
            })
          );
        }

        if (fingerprintResult?.components?.canvas?.value?.text) {
          hashPromises.push(
            hashBase64Image(fingerprintResult.components.canvas.value.text).then((hash) => {
              hashedText = hash;
            })
          );
        }

        return Promise.all(hashPromises).then(() => {
          const device_token = localStorage.getItem("device_token");

          // Build modified components without mutating the original fingerprintResult
          let modifiedComponents = fingerprintResult.components;
          if (fingerprintResult?.components?.canvas?.value) {
            modifiedComponents = {
              ...fingerprintResult.components,
              canvas: {
                ...fingerprintResult.components.canvas,
                value: {
                  ...fingerprintResult.components.canvas.value,
                  geometry: hashedGeometry,
                  text: hashedText,
                },
              },
            };
          }

          const payload = {
            request_id: crypto.randomUUID(),
            referer: `${window.location.host}${window.location.pathname}`,
            site_id: SITE_ID,
            timestamp: Date.now(),
            cookie_device_id: cookie_device_id,
            device_token: device_token,
            Thumbmark__device_id: thumbmarkResult.thumbmark,
            FingerprintJS__device_id: fingerprintResult.visitorId,
            user_data: {
              user_id: user?.id,
              user_email: user?.email,
              user_full_name: user?.fullName,
              user_organization_id: user?.organizationId,
              user_organization_name: user?.organizationName,
              user_organization_internal: user?.isInternal,
            },
            sdk_type: SDK_TYPE,
            sdk_version: SDK_VERSION,
            schema_version: SCHEMA_VERSION,
            attributes: {
              Thumbmark: thumbmarkResult.components,
              FingerprintJS: modifiedComponents
            }
          };

          if (!isEncryptionSupported()) {
            return fetch(DEVICE_COLLECT_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
            });
          }

          return encryptPayload(payload).then((encryptedData) => {
            return fetch(DEVICE_COLLECT_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'pipl-encrypted': 'true',
              },
              body: JSON.stringify(encryptedData),
            });
          });
        });
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Device Intelligence collection failed (this is expected in some dev environments):', error?.message || error);
        }
      });
  }, [user]);

  useEffect(() => {
    collectAndSendDeviceIntelligence();
  }, [collectAndSendDeviceIntelligence]);

  return { collectAndSendDeviceIntelligence };
};

export default useDeviceIntelligence;
